/**
 * Yandex SmartCaptcha виджет (AN-030).
 * Без site key (dev/bypass) — виджет скрыт, токен пустой.
 */

import { useEffect, useId, useRef, useState } from "react";
import { loadPublicConfig } from "../../api/public-config";

const SCRIPT_SRC = "https://smartcaptcha.yandexcloud.net/captcha.js";

declare global {
  interface Window {
    smartCaptcha?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          hl?: string;
        },
      ) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

type SmartCaptchaFieldProps = {
  onToken: (token: string) => void;
  disabled?: boolean;
};

export function SmartCaptchaField({ onToken, disabled = false }: SmartCaptchaFieldProps) {
  const containerId = useId().replace(/:/g, "");
  const widgetIdRef = useRef<number | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadPublicConfig()
      .then((config) => {
        if (!cancelled) {
          setSiteKey(config.captchaSiteKey.trim());
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSiteKey("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (siteKey === null) {
      return;
    }
    if (!siteKey) {
      onTokenRef.current("");
      return;
    }

    let cancelled = false;

    function mount(): void {
      if (cancelled || !window.smartCaptcha || siteKey === null || siteKey === "") {
        return;
      }
      const el = document.getElementById(containerId);
      if (!el || widgetIdRef.current !== null) {
        return;
      }
      const key = siteKey;
      widgetIdRef.current = window.smartCaptcha.render(el, {
        sitekey: key,
        hl: "ru",
        callback: (token) => onTokenRef.current(token),
      });
    }

    if (window.smartCaptcha) {
      mount();
      return () => {
        cancelled = true;
      };
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", mount);
      return () => {
        cancelled = true;
        existing.removeEventListener("load", mount);
      };
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.defer = true;
    script.onload = () => mount();
    script.onerror = () => setError("Не удалось загрузить CAPTCHA");
    document.head.appendChild(script);
    return () => {
      cancelled = true;
    };
  }, [siteKey, containerId]);

  if (siteKey === null) {
    return <p className="wizard-step__status">Загрузка защиты формы…</p>;
  }

  if (!siteKey) {
    return null;
  }

  return (
    <div className="smart-captcha" aria-disabled={disabled || undefined}>
      <div id={containerId} />
      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
